/* PrescriptionComplianceBlock — Step 6 Review & Submit gate.
 *
 * iter98.99au — DRASTICALLY simplified per user direction:
 *   "We should have validations to the Doctor's e-signature in the Your
 *    Profile so that without adding the signature they cannot move ahead.
 *    Make it mandatory. We will just fetch that signature onto the Rx's.
 *    No need to have the e-signature mess that we went thru yesterday
 *    on the last step."
 *
 * The Step 6 block used to host a fat E-Signature card with Draw /
 * Upload / Type tabs, a "Save for future Rxs" toggle, and a separate
 * profile-reuse checkbox — three different signing modes the doctor
 * juggled inside a single case wizard. All gone. The block is now:
 *
 *   1. A single-line "I confirm all" consent affordance (unchanged from
 *      iter98.99ao — opens a Consent Policy dialog with the full 4-point
 *      legal text).
 *   2. A read-only "Signature on file" row that mirrors the doctor's
 *      saved profile signature. If the profile signature is missing we
 *      render a hard block banner with a CTA to /doctor/profile.
 *
 * The wizard's "Sign & Submit to Lab" CTA still triggers signNow() via
 * signApiRef — which now always uses `signature_method=profile_reuse`
 * and embeds the doctor's `doctor_signature_data_url` into the audit
 * payload. Backend `/cases/{id}/prescription/sign` is unchanged.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
    CheckCircle,
    Warning,
    Shield,
    ScrollIcon as ScrollText,
    PencilSimpleLine,
    ArrowSquareOut,
} from "@phosphor-icons/react";

export default function PrescriptionComplianceBlock({
    caseId,
    doctorProfile,            // { name, dci_registration_number, doctor_signature_data_url, qualifications }
    initialCompliance = null, // server-side snapshot if already signed
    onSignedChange,           // (boolean isFullySigned) — toggles outer submit gate
    onComplianceUpdate,       // (compliance) — bubbles up the fresh server snapshot
    signApiRef,               // wizard reads from this ref to call signNow() at submit time
}) {
    /* ── Profile signature ─ the ONE source of truth ─────────────── */
    const profileSig = doctorProfile?.doctor_signature_data_url || "";
    const hasProfileSig = !!profileSig;

    /* ── Checklist state ─────────────────────────────────────────── */
    const [ck, setCk] = useState(() => ({
        treatment_plan_approved: !!initialCompliance?.checklist?.treatment_plan_approved,
        treatment_plan_approved_at: initialCompliance?.checklist?.treatment_plan_approved_at || null,
        patient_consent_confirmed: !!initialCompliance?.checklist?.patient_consent_confirmed,
        patient_consent_confirmed_at: initialCompliance?.checklist?.patient_consent_confirmed_at || null,
        prescription_reviewed: !!initialCompliance?.checklist?.prescription_reviewed,
        prescription_reviewed_at: initialCompliance?.checklist?.prescription_reviewed_at || null,
        compliance_acknowledged: !!initialCompliance?.checklist?.compliance_acknowledged,
        compliance_acknowledged_at: initialCompliance?.checklist?.compliance_acknowledged_at || null,
    }));

    const toggleAllCk = (next) => {
        const now = next ? new Date().toISOString() : null;
        setCk({
            treatment_plan_approved: next,
            treatment_plan_approved_at: now,
            patient_consent_confirmed: next,
            patient_consent_confirmed_at: now,
            prescription_reviewed: next,
            prescription_reviewed_at: now,
            compliance_acknowledged: next,
            compliance_acknowledged_at: now,
        });
    };

    /* ── Network ─────────────────────────────────────────────────── */
    const [savedSnapshot, setSavedSnapshot] = useState(initialCompliance);

    /* ── Derived state ───────────────────────────────────────────── */
    const allTicked = ck.treatment_plan_approved && ck.patient_consent_confirmed
        && ck.prescription_reviewed && ck.compliance_acknowledged;
    const isAlreadySigned = !!savedSnapshot?.signed_at;
    const fullyReady = useMemo(() => hasProfileSig && allTicked, [hasProfileSig, allTicked]);

    useEffect(() => {
        if (onSignedChange) onSignedChange(fullyReady);
    }, [fullyReady, onSignedChange]);

    /* ── Signing flow (called by wizard via signApiRef) ──────────── */
    const handleSign = async () => {
        if (!hasProfileSig) {
            toast.error("Add your e-signature to your profile to submit cases.");
            return false;
        }
        if (!allTicked) {
            toast.error("Agree to the Consent Policy first.");
            return false;
        }
        try {
            const { data } = await api.post(`/cases/${caseId}/prescription/sign`, {
                signature_data_url: profileSig,
                signature_typed_name: null,
                signature_method: "profile_reuse",
                checklist: ck,
            });
            setSavedSnapshot(data.prescription_compliance);
            if (onComplianceUpdate) onComplianceUpdate(data.prescription_compliance);
            return true;
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Failed to sign prescription");
            return false;
        }
    };

    /* Expose imperative signNow() to the parent wizard. Intentionally
       no dep array — we want this to re-bind every render so closures
       capture fresh state. iter98.99bo — also expose `tickAllConsents`
       so the wizard's Step 6 demo-fill button can pre-check the four
       boxes when staging a demo case. */
    useEffect(() => {
        if (!signApiRef) return;
        signApiRef.current = {
            ready: fullyReady,
            isAlreadySigned,
            signNow: async () => {
                if (isAlreadySigned) return true;
                return await handleSign();
            },
            tickAllConsents: () => toggleAllCk(true),
        };
    });

    /* ── DCI warning ─────────────────────────────────────────────── */
    const dciMissing = !doctorProfile?.dci_registration_number;

    /* ── Render ──────────────────────────────────────────────────── */
    return (
        <div className="space-y-3" data-testid="case-wizard-prescription-compliance">

            {/* ─── Missing profile signature — HARD BLOCK ─── */}
            {!hasProfileSig && (
                <div
                    className="rounded-xl border-2 border-rose-300 bg-rose-50 p-4"
                    data-testid="profile-signature-missing-block"
                >
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 w-9 h-9 rounded-full bg-rose-100 grid place-items-center">
                            <Warning size={18} weight="fill" className="text-rose-700" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-display text-sm font-bold text-rose-900">
                                Add your e-signature to your profile to submit this case
                            </div>
                            <p className="text-[12px] text-rose-900/85 leading-snug mt-1">
                                Your e-signature is fetched from your Doctor Profile and auto-applied to every prescription. It&apos;s a one-time setup — once added, we&apos;ll never ask you to re-sign on the wizard again.
                            </p>
                            <Link
                                to="/doctor/profile?tab=profile#branding"
                                className="inline-flex items-center gap-1.5 mt-3 px-3.5 py-2 rounded-full bg-rose-700 hover:bg-rose-800 text-white text-[12px] font-semibold shadow-sm transition-colors"
                                data-testid="profile-signature-missing-cta"
                            >
                                <PencilSimpleLine size={13} weight="bold" />
                                Add signature to profile
                                <ArrowSquareOut size={11} weight="bold" />
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {dciMissing && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 flex gap-2.5 text-[12px]" data-testid="dci-missing-warn">
                    <Warning size={16} weight="duotone" className="text-amber-700 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold text-amber-900">DCI / State Council registration number is missing on your profile.</div>
                        <div className="text-amber-800 mt-0.5">Your prescription PDF will show &ldquo;Not yet provided&rdquo; in that field. Update your profile to print your registration number on every patient document.</div>
                        <Link
                            to="/doctor/profile#branding"
                            className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-amber-900 hover:text-amber-700 underline underline-offset-2"
                            data-testid="dci-missing-update-link"
                        >
                            Add it to my profile →
                        </Link>
                    </div>
                </div>
            )}

            {/* ─── Block 1 · Consent Policy + Single Acknowledgement ───
                iter101.74 — Redesigned to feel premium and intentional.
                The old flat one-liner has been replaced with a two-row
                card: a small "Consent required" eyebrow + a large
                affirmation sentence on row one, and a soft callout row
                two that expands the four things the doctor is agreeing
                to as inline chips. The whole card flips to an emerald
                "signed" state once ticked so the doctor gets clear
                visual feedback the gate has been cleared. */}
            <div
                className={`rounded-2xl border transition-all shadow-[0_1px_0_rgba(15,23,42,0.02)] ${
                    allTicked
                        ? "border-emerald-300 bg-gradient-to-br from-emerald-50/70 via-emerald-50/25 to-white"
                        : "border-border/80 bg-card hover:border-primary/40"
                }`}
                data-testid="prescription-checklist-card"
            >
                {/* Row 1 — the affirmation. Big checkbox, big statement. */}
                <label
                    className="flex items-start gap-3.5 px-4 py-3.5 cursor-pointer"
                    data-testid="ck-confirm-all"
                >
                    <span
                        className={`shrink-0 mt-0.5 w-6 h-6 rounded-md grid place-items-center transition-all ${
                            allTicked
                                ? "bg-emerald-600 text-white shadow-[0_2px_8px_rgba(16,185,129,0.35)]"
                                : "bg-white border-2 border-primary/40 hover:border-primary/70"
                        }`}
                        aria-hidden="true"
                    >
                        {allTicked && <CheckCircle size={16} weight="fill" />}
                    </span>
                    <input
                        type="checkbox"
                        checked={allTicked}
                        onChange={(e) => toggleAllCk(e.target.checked)}
                        className="sr-only"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <div
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-[0.14em] ${
                                    allTicked
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-primary/10 text-primary"
                                }`}
                            >
                                <Shield size={9} weight="fill" />
                                {allTicked ? "Consent signed" : "Consent required"}
                            </div>
                            {allTicked && (
                                <span
                                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 uppercase tracking-wider"
                                    data-testid="consent-acknowledged-badge"
                                >
                                    <CheckCircle size={11} weight="fill" /> Acknowledged
                                </span>
                            )}
                        </div>
                        <div className="font-display text-[14px] font-semibold text-foreground leading-snug">
                            I agree to the{" "}
                            <Dialog>
                                <DialogTrigger asChild>
                                    <button
                                        type="button"
                                        onClick={(e) => e.stopPropagation()}
                                        className="text-primary underline underline-offset-2 hover:text-primary/80 inline-flex items-center gap-1"
                                        data-testid="consent-policy-trigger"
                                    >
                                        <ScrollText size={13} weight="duotone" />
                                        Consent Policy
                                    </button>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg" data-testid="consent-policy-dialog">
                                    <DialogHeader>
                                        <DialogTitle className="font-display flex items-center gap-2 text-base">
                                            <Shield size={16} weight="duotone" className="text-primary" />
                                            Consent Policy
                                        </DialogTitle>
                                        <DialogDescription className="text-[12px]">
                                            By submitting this prescription you confirm all 4 affirmations below. Each is timestamped individually in your audit log per IT Act / NMC Guidelines.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-2.5 text-[12.5px] text-foreground/90 leading-relaxed pt-2">
                                        <PolicyRow n="1" title="Treatment Plan Approved">I have reviewed and approved the treatment plan for this patient.</PolicyRow>
                                        <PolicyRow n="2" title="Patient Consent Confirmed">I have obtained explicit patient consent for this treatment plan.</PolicyRow>
                                        <PolicyRow n="3" title="Prescription Reviewed">I have reviewed and certified the generated prescription for accuracy.</PolicyRow>
                                        <PolicyRow n="4" title="Compliance Acknowledged">This prescription complies with Indian medical regulations (IT Act, NMC Guidelines).</PolicyRow>
                                    </div>
                                </DialogContent>
                            </Dialog>
                            {" "}— covering the four affirmations below.
                        </div>
                    </div>
                </label>

                {/* Row 2 — the four affirmations laid out as chips so the
                    doctor sees exactly what they've agreed to without
                    opening the dialog. Emerald ticks once the whole
                    card is signed. */}
                <div className="px-4 pb-3.5 pt-1 flex flex-wrap gap-1.5">
                    {[
                        "Treatment Plan",
                        "Patient Consent",
                        "Rx Review",
                        "Regulatory Compliance",
                    ].map((label) => (
                        <span
                            key={label}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${
                                allTicked
                                    ? "bg-white text-emerald-800 ring-emerald-200"
                                    : "bg-muted/40 text-muted-foreground ring-border/60"
                            }`}
                            data-testid={`consent-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <CheckCircle
                                size={10}
                                weight="fill"
                                className={allTicked ? "text-emerald-600" : "text-muted-foreground/50"}
                            />
                            {label}
                        </span>
                    ))}
                </div>
            </div>

            {/* iter98.99cu — User direction: "Can we not hide the signature
                section in the case page while doctor submits the case?
                We just need to fetch the signature on the Rx automatically."
                The visual "Signature on file" confirmation card (with the
                signature preview + Manage link) has been removed. The
                signature is still:
                  • validated via the hard-block banner above when missing,
                  • auto-embedded into the Rx PDF + audit payload at submit
                    time via signNow() → signature_method=profile_reuse.
                No behavioural change — only the redundant visual surface
                is dropped to reduce friction on the final wizard step. */}

            {/* ─── Sign status copy ─── */}
            <div className="px-1">
                <div className="text-[11.5px] text-muted-foreground" data-testid="prescription-sign-status">
                    {!hasProfileSig && "Add your e-signature to your profile above to enable Submit."}
                    {hasProfileSig && !allTicked && "Agree to the Consent Policy above to enable Submit."}
                    {hasProfileSig && allTicked && !isAlreadySigned && (
                        <span>Ready — clicking <b>Sign &amp; Submit to Lab</b> will lock this Rx into the audit log and send the case.</span>
                    )}
                    {isAlreadySigned && (
                        <span className="text-emerald-700 font-semibold">
                            ✓ Signed &amp; logged · You can now submit to lab.
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}


function PolicyRow({ n, title, children }) {
    return (
        <div className="flex gap-2.5">
            <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary grid place-items-center text-[11px] font-bold mt-0.5">{n}</span>
            <div>
                <div className="font-semibold">{title}</div>
                <div className="text-muted-foreground text-[12px]">{children}</div>
            </div>
        </div>
    );
}
